/**
 * Intelligence helpers: Extract structured data from JSONB fields
 * stored in os_linkedin_drafts (decision_trace, analysis_report, strategy).
 */

// ─────────────────────────────────────────────────────────────────────────
// Types for parsed intelligence
// ─────────────────────────────────────────────────────────────────────────

export type FunnelStage =
  | 'qualifying'
  | 'needs_followup'
  | 'next_step_defined'
  | 'meeting_booked'
  | 'graceful_exit'
  | 'blocked'
  | 'unknown';

export type BANTStatus = 'unknown' | 'qualified' | 'disqualified';

export interface BANTQualification {
  budget: BANTStatus;
  authority: BANTStatus;
  need: BANTStatus;
  timeline: BANTStatus;
}

export interface MomentumData {
  score: number;
  trend: 'heating' | 'cooling' | 'stable';
  signals: string[];
}

export interface ConversationSignal {
  signal: string;
  evidence: string;
}

export interface DISCProfile {
  primary: 'D' | 'I' | 'S' | 'C';
  secondary: 'D' | 'I' | 'S' | 'C' | null;
  confidence: number;
  communicationGuide?: {
    do: string[];
    dont: string[];
    tone: string;
  };
}

export interface LeadEnrichment {
  industry?: string;
  companySize?: string;
  seniority?: string;
  decisionMaker?: boolean;
  communicationStyle?: string;
}

export interface ParsedGameplan {
  funnelStage: FunnelStage;
  bant: BANTQualification;
  momentum: MomentumData;
  methodology: string;
  methodologyReasoning: string;
  tactics: string[];
  confidence: number;
  nextStep: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Parsers
// ─────────────────────────────────────────────────────────────────────────

const VALID_FUNNEL_STAGES: FunnelStage[] = [
  'qualifying', 'needs_followup', 'next_step_defined',
  'meeting_booked', 'graceful_exit', 'blocked',
];

export const parseFunnelStage = (decisionTrace: Record<string, unknown> | null): FunnelStage => {
  if (!decisionTrace) return 'unknown';
  const stage = decisionTrace.funnelStage as string | undefined;
  if (stage && VALID_FUNNEL_STAGES.includes(stage as FunnelStage)) {
    return stage as FunnelStage;
  }
  return 'unknown';
};

export const parseBANT = (decisionTrace: Record<string, unknown> | null): BANTQualification => {
  const defaults: BANTQualification = {
    budget: 'unknown', authority: 'unknown', need: 'unknown', timeline: 'unknown',
  };
  if (!decisionTrace) return defaults;

  const bantStr = decisionTrace.funnelBANT as string | undefined;
  if (!bantStr || typeof bantStr !== 'string') return defaults;

  const parseField = (field: string): BANTStatus => {
    const match = bantStr.match(new RegExp(`${field}\\(([YN?])\\)`));
    if (!match) return 'unknown';
    if (match[1] === 'Y') return 'qualified';
    if (match[1] === 'N') return 'disqualified';
    return 'unknown';
  };

  return {
    budget: parseField('Budget'),
    authority: parseField('Authority'),
    need: parseField('Need'),
    timeline: parseField('Timeline'),
  };
};

export const parseMomentum = (decisionTrace: Record<string, unknown> | null): MomentumData => {
  const defaults: MomentumData = { score: 0, trend: 'stable', signals: [] };
  if (!decisionTrace) return defaults;

  const momentum = decisionTrace.momentum as Record<string, unknown> | undefined;
  if (!momentum) return defaults;

  return {
    score: typeof momentum.score === 'number' ? momentum.score : 0,
    trend: (['heating', 'cooling', 'stable'].includes(momentum.trend as string)
      ? momentum.trend as 'heating' | 'cooling' | 'stable'
      : 'stable'),
    signals: Array.isArray(momentum.signals) ? momentum.signals.map(String) : [],
  };
};

export const parseSignals = (analysisReport: Record<string, unknown> | null): ConversationSignal[] => {
  if (!analysisReport) return [];
  const signals = analysisReport.signals as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(signals)) return [];
  return signals.map((s) => ({
    signal: String(s.signal || ''),
    evidence: String(s.evidence || ''),
  }));
};

export const parseDISC = (analysisReport: Record<string, unknown> | null): DISCProfile | null => {
  if (!analysisReport) return null;
  const disc = analysisReport.discProfile as Record<string, unknown> | undefined;
  if (!disc || !disc.primary) return null;
  const guide = disc.communicationGuide as Record<string, unknown> | undefined;
  return {
    primary: disc.primary as DISCProfile['primary'],
    secondary: (disc.secondary as DISCProfile['secondary']) || null,
    confidence: typeof disc.confidence === 'number' ? disc.confidence : 0,
    communicationGuide: guide ? {
      do: Array.isArray(guide.do) ? guide.do.map(String) : [],
      dont: Array.isArray(guide.dont) ? guide.dont.map(String) : [],
      tone: String(guide.tone || ''),
    } : undefined,
  };
};

export const parseLeadEnrichment = (analysisReport: Record<string, unknown> | null): LeadEnrichment => {
  if (!analysisReport) return {};
  const profile = analysisReport.leadProfile as Record<string, unknown> | undefined;
  if (!profile) return {};
  return {
    industry: (profile.industry as string) || (analysisReport.industryInference as Record<string, unknown>)?.industry as string | undefined,
    companySize: (profile.companySize as string) || (analysisReport.companySizeInference as Record<string, unknown>)?.size as string | undefined,
    seniority: profile.seniority as string | undefined,
    decisionMaker: profile.decisionMaker as boolean | undefined,
    communicationStyle: profile.communicationStyle as string | undefined,
  };
};

export const parseStrategy = (
  strategy: Record<string, unknown> | null
): { methodology: string; reasoning: string; tactics: string[]; confidence: number } => {
  if (!strategy) return { methodology: 'unknown', reasoning: '', tactics: [], confidence: 0 };
  return {
    methodology: String(strategy.methodology || 'unknown'),
    reasoning: String(strategy.reasoning || ''),
    tactics: Array.isArray(strategy.tactics) ? strategy.tactics.map(String) : [],
    confidence: typeof strategy.confidence === 'number' ? strategy.confidence : 0,
  };
};

export const extractGameplan = (
  decisionTrace: Record<string, unknown> | null,
  strategy: Record<string, unknown> | null,
  methodologyUsed?: string,
  confidence?: number,
): ParsedGameplan => {
  const parsed = parseStrategy(strategy);
  const momentum = parseMomentum(decisionTrace);
  const funnelStage = parseFunnelStage(decisionTrace);

  return {
    funnelStage,
    bant: parseBANT(decisionTrace),
    momentum,
    methodology: parsed.methodology !== 'unknown' ? parsed.methodology : (methodologyUsed || 'unknown'),
    methodologyReasoning: parsed.reasoning,
    tactics: parsed.tactics,
    confidence: parsed.confidence || confidence || 0,
    nextStep: getFunnelNextStep(funnelStage),
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Display helpers
// ─────────────────────────────────────────────────────────────────────────

export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  qualifying: 'Qualifying',
  needs_followup: 'Follow-up',
  next_step_defined: 'Next Step',
  meeting_booked: 'Meeting',
  graceful_exit: 'Exit',
  blocked: 'Blocked',
  unknown: 'New',
};

export const FUNNEL_STAGE_COLORS: Record<FunnelStage, string> = {
  qualifying: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  needs_followup: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  next_step_defined: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  meeting_booked: 'bg-green-500/15 text-green-400 border-green-500/30',
  graceful_exit: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  blocked: 'bg-red-500/15 text-red-400 border-red-500/30',
  unknown: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

export const MOMENTUM_COLORS: Record<string, string> = {
  heating: 'text-green-400',
  cooling: 'text-amber-400',
  stable: 'text-muted-foreground',
};

export const BANT_COLORS: Record<BANTStatus, string> = {
  qualified: 'bg-green-500',
  disqualified: 'bg-red-500',
  unknown: 'bg-gray-600',
};

export const DISC_COLORS: Record<string, string> = {
  D: 'bg-red-500/15 text-red-400 border-red-500/30',
  I: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  S: 'bg-green-500/15 text-green-400 border-green-500/30',
  C: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
};

const getFunnelNextStep = (stage: FunnelStage): string => {
  switch (stage) {
    case 'qualifying': return 'Ask discovery questions. Explore BANT dimensions.';
    case 'needs_followup': return 'Send value-add follow-up. Respect their timing.';
    case 'next_step_defined': return 'Maintain momentum. Confirm next step logistics.';
    case 'meeting_booked': return 'Confirm logistics. Prepare meeting brief.';
    case 'graceful_exit': return 'Thank them. Ask for referral.';
    case 'blocked': return 'Stop outreach. Do not contact.';
    default: return 'Continue qualifying. Build rapport.';
  }
};

export const buildDealNarrative = (
  leadName: string,
  leadTitle: string | null,
  leadCompany: string | null,
  gameplan: ParsedGameplan,
  disc: DISCProfile | null,
  exchangeCount: number,
): string => {
  const parts: string[] = [];

  // Identity
  const identity = [leadName];
  if (leadTitle) identity.push(leadTitle);
  if (leadCompany) identity.push(leadCompany);
  if (disc) identity.push(`${disc.primary}-type`);
  parts.push(`${identity.join(', ')} is in ${FUNNEL_STAGE_LABELS[gameplan.funnelStage]} stage after ${exchangeCount} exchange${exchangeCount !== 1 ? 's' : ''}.`);

  // Methodology narrative
  if (gameplan.methodologyReasoning) {
    parts.push(gameplan.methodologyReasoning.endsWith('.') ? gameplan.methodologyReasoning : `${gameplan.methodologyReasoning}.`);
  } else if (gameplan.methodology !== 'unknown') {
    parts.push(`Using ${gameplan.methodology.charAt(0).toUpperCase() + gameplan.methodology.slice(1)} approach.`);
  }

  // Momentum
  const trendWord = gameplan.momentum.trend === 'heating' ? 'rising' : gameplan.momentum.trend === 'cooling' ? 'cooling' : 'steady';
  parts.push(`Momentum is ${trendWord}.`);

  // Next step
  parts.push(`Recommended: ${gameplan.nextStep}`);

  return parts.join(' ');
};

/** Compute a 0-100 Tacit Score from existing data */
export const computeTacitScore = (
  constraintValidation: { score: number; total: number } | null,
  confidence: number,
  momentum: MomentumData,
): number => {
  // Constraint validation: 0-7 -> 0-40 weight
  const constraintScore = constraintValidation
    ? (constraintValidation.score / constraintValidation.total) * 40
    : 20; // default if no validation
  // Methodology confidence: 0-1 -> 0-30
  const confidenceScore = confidence * 30;
  // Momentum alignment: 0-1 -> 0-30
  const momentumScore = momentum.score * 30;

  return Math.round(constraintScore + confidenceScore + momentumScore);
};

/** Format relative time */
export const formatRelativeTime = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

/** Recency color class */
export const getRecencyColor = (dateStr: string): string => {
  const hours = (Date.now() - new Date(dateStr).getTime()) / 3600000;
  if (hours < 4) return 'text-green-400';
  if (hours < 24) return 'text-amber-400';
  return 'text-red-400';
};
